import type { Facture, Journal, MoyenEncaissement, MoyenReglement } from './types'

/**
 * Règlement d'une facture (migration 0023 : `invoices.paid_at`,
 * `invoices.payment_method`).
 *
 * Module PUR (aucun accès réseau) : tout ce qui se teste sans base est ici.
 */

/** Les moyens proposés, dans l'ordre d'affichage. Les valeurs sont celles de la base. */
export const MOYENS_REGLEMENT: readonly { valeur: MoyenReglement; libelle: string }[] = [
  { valeur: 'especes', libelle: 'Espèces' },
  { valeur: 'wave', libelle: 'Wave' },
  { valeur: 'orange_money', libelle: 'Orange Money' },
  { valeur: 'virement', libelle: 'Virement' },
  { valeur: 'cheque', libelle: 'Chèque' },
]

export function libelleMoyen(m: string | null | undefined): string {
  return MOYENS_REGLEMENT.find((x) => x.valeur === m)?.libelle ?? ''
}

export function moyenValide(m: string): m is MoyenReglement {
  return MOYENS_REGLEMENT.some((x) => x.valeur === m)
}

/**
 * Une facture est RÉGLÉE quand elle porte une date de règlement — pas quand
 * son statut dit « payée » : le statut seul ne dit pas QUAND, et sans date on
 * ne peut ni dater l'écriture ni sortir la créance de la bonne période.
 * Une facture annulée n'est jamais réglée.
 */
export function estReglee(f: Pick<Facture, 'status' | 'paid_at'>): boolean {
  return (f.status === 'issued' || f.status === 'paid') && !!f.paid_at
}

/**
 * Compte de trésorerie débité et journal, selon le moyen (plan SYSCOHADA de la console). Valable pour le règlement
 * d'une facture comme pour l'encaissement d'une course : « autre » (virement…) et tout moyen inconnu → banque.
 */
export function tresoreriePourMoyen(m: MoyenReglement | MoyenEncaissement | null | undefined): { compte: string; journal: Journal } {
  switch (m) {
    case 'especes':
      return { compte: '571', journal: 'CA' }
    case 'wave':
    case 'orange_money':
      /* Même compte que l'encaissement mobile money des courses de particuliers. */
      return { compte: '551', journal: 'BQ' }
    default:
      /* Virement, chèque — et règlement sans moyen connu : la banque. */
      return { compte: '521', journal: 'BQ' }
  }
}

/** Motifs de refus d'un règlement avant tout appel réseau (vide = acceptable). */
export function controlerReglement(
  e: { date: string; moyen: string; issue_date: string },
  aujourdHui: string,
  verrou: string | null = null,
): string[] {
  const motifs: string[] = []
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(e.date) && !Number.isNaN(new Date(`${e.date}T00:00:00Z`).getTime())
  if (!dateOk) motifs.push('Date de règlement invalide.')
  else {
    if (e.date > aujourdHui) motifs.push('La date de règlement ne peut pas être dans le futur.')
    if (e.date < e.issue_date.slice(0, 10)) motifs.push("Le règlement ne peut pas précéder l'émission de la facture.")
    if (verrou && e.date <= verrou)
      motifs.push(`Le règlement ne peut pas être daté d'une période clôturée (jusqu'au ${dateFr(verrou)}).`)
  }
  if (!moyenValide(e.moyen)) motifs.push('Choisissez un moyen de règlement.')
  return motifs
}

/** Annuler un règlement n'est possible que si sa date est dans une période ouverte. */
export function controlerAnnulationReglement(paidAt: string | null, verrou: string | null): string[] {
  if (verrou && paidAt && paidAt <= verrou)
    return [`Ce règlement est daté d'une période clôturée (jusqu'au ${dateFr(verrou)}) : rouvrez l'exercice pour l'annuler.`]
  return []
}

function dateFr(iso: string): string {
  const [a, m, j] = iso.slice(0, 10).split('-')
  return `${j}/${m}/${a}`
}

export const MESSAGE_MIGRATION_0023 = 'Disponible après la mise à jour de la base (migration 0023).'

/**
 * Erreur Postgres / PostgREST en français.
 * - colonne absente (42703, PGRST204) : la migration 0023 n'est pas poussée ;
 * - `periode_cloturee` : le `detail` écrit par le déclencheur (arrive dans `details`).
 */
export function traduireErreurReglement(erreur: unknown): string {
  const o = (erreur && typeof erreur === 'object' ? erreur : {}) as {
    message?: unknown
    details?: unknown
    detail?: unknown
    code?: unknown
  }
  const message = typeof o.message === 'string' ? o.message : typeof erreur === 'string' ? erreur : ''
  const detail = typeof o.details === 'string' ? o.details : typeof o.detail === 'string' ? o.detail : ''
  const code = typeof o.code === 'string' ? o.code : ''
  if (colonneReglementAbsente(erreur)) return MESSAGE_MIGRATION_0023
  if (/periode_cloturee/.test(message) || /periode_cloturee/.test(code))
    return detail || 'Cette période est clôturée : rouvrez l’exercice pour la modifier.'
  return message || 'Erreur inconnue.'
}

/** Vrai si l'erreur dit que `paid_at` / `payment_method` n'existent pas (migration non poussée). */
export function colonneReglementAbsente(erreur: unknown): boolean {
  const o = (erreur && typeof erreur === 'object' ? erreur : {}) as { message?: unknown; code?: unknown }
  const message = typeof o.message === 'string' ? o.message : typeof erreur === 'string' ? erreur : ''
  const code = typeof o.code === 'string' ? o.code : ''
  return code === '42703' || code === 'PGRST204' || (/paid_at|payment_method/.test(message) && /column|colonne|schema cache/i.test(message))
}
